/*
 * ahc-gwp.js: AHC gift with purchase, automatic.
 *
 * Once the bag's counting items reach the threshold, the gift goes in by itself
 * (line property _gift: auto). If the bag drops back under, the gift we added
 * comes out again, so nobody pays for it. If the shopper removes it while still
 * over, that's a no: the cart remembers it (cart attribute _gift_declined) and the
 * drawer and product page offer to add it back instead of pushing it in again.
 *
 * A bag the shopper added themselves (no _gift property) is never touched.
 * The automatic discount in Shopify admin does the pricing; this only moves the line.
 *
 * Which lines count depends on collections, which only Liquid can see, so every
 * check reads sections/ahc-gwp-state.liquid after a cart change, whoever made it.
 * The same read keeps the product page block (snippets/ahc-gwp-pdp.liquid) live.
 */
(function () {
  'use strict';

  var CFG = window.ahcGwp;
  if (!CFG || !CFG.enabled || window.__ahcGwp) return;
  window.__ahcGwp = true;

  var CART_RE = /\/cart\/(add|change|update|clear)/;
  var LAST_KEY = 'ahc-gwp-last';
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var origFetch = window.fetch.bind(window);

  /* ---- Watch every cart request on the page (theme, quick add, apps). ---- */
  function watchCart(fn) {
    try {
      window.fetch = function (input, init) {
        var result = origFetch(input, init);
        try {
          var url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input && input.url) || '';
          if (CART_RE.test(url)) result.then(fn, function () {});
        } catch (e) {}
        return result;
      };
      var open = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          if (CART_RE.test(String(url || ''))) this.addEventListener('loadend', fn);
        } catch (e) {}
        return open.apply(this, arguments);
      };
    } catch (e) {}
  }

  /* ---- Read the bag. ---- */
  function readState() {
    return origFetch(root + '?sections=ahc-gwp-state', { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) {
        return r.json();
      })
      .then(function (json) {
        var html = json && json['ahc-gwp-state'];
        if (!html) return null;
        var el = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-ahc-gwp-state]');
        return el ? JSON.parse(el.textContent) : null;
      });
  }

  function lastSeen() {
    try {
      return JSON.parse(sessionStorage.getItem(LAST_KEY) || '{}');
    } catch (e) {
      return {};
    }
  }
  function remember(hadAuto) {
    try {
      sessionStorage.setItem(LAST_KEY, JSON.stringify({ hadAuto: hadAuto }));
    } catch (e) {}
  }

  /* ---- Write to the bag, then redraw what the theme shows. ---- */
  function sections() {
    try {
      // cart.js keeps the theme's own list of what to redraw.
      return typeof sectionsToRender !== 'undefined' ? sectionsToRender : [];
    } catch (e) {
      return [];
    }
  }

  function post(url, body) {
    var list = sections();
    if (list.length) {
      body.sections = list.map(function (s) {
        return s.section;
      });
      body.sections_url = window.location.pathname;
    }
    return origFetch(root + url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    })
      .then(function (r) {
        return r.json().then(function (j) {
          return { ok: r.ok, json: j };
        });
      })
      .then(function (res) {
        if (res.ok) redraw(res.json);
        return res;
      });
  }

  function redraw(json) {
    var list = sections();
    if (json && json.sections) {
      list.forEach(function (s) {
        var html = json.sections[s.section];
        var target = document.querySelector(s.selector) || document.querySelector(s.id);
        if (!html || !target) return;
        var src = new DOMParser().parseFromString(html, 'text/html').querySelector(s.selector);
        if (src) target.innerHTML = src.innerHTML;
      });
    }
    try {
      if (typeof updateCartCounters === 'function') updateCartCounters();
      if (typeof updateFreeShipping === 'function') updateFreeShipping();
      if (typeof handleCartInputNames === 'function') handleCartInputNames();
    } catch (e) {}
  }

  function addGift(variant) {
    return post('cart/add.js', { items: [{ id: variant, quantity: 1, properties: { _gift: 'auto' } }] });
  }
  function removeLines(lines) {
    var updates = {};
    lines.forEach(function (l) {
      updates[l.key] = 0;
    });
    return post('cart/update.js', { updates: updates });
  }
  function setDeclined(on) {
    return post('cart/update.js', { attributes: { _gift_declined: on ? '1' : '' } });
  }

  /* ---- Product page block. ---- */
  var pdpBlocks = [];

  function money(cents, cur) {
    return (cur || 'HK$') + (cents / 100).toLocaleString('en-HK', { maximumFractionDigits: 2 });
  }

  function pdpQty(el) {
    var form = el.dataset.form;
    var input =
      document.querySelector('input[name="quantity"][form="' + form + '"]') ||
      document.querySelector('#' + form + ' input[name="quantity"]');
    var q = input ? parseInt(input.value, 10) : 1;
    return q > 0 ? q : 1;
  }

  function pdpRender(el) {
    var d = el.dataset;
    var S = el.__strings || {};
    var threshold = Number(d.threshold);
    var eligible = Number(d.eligible);
    var price = Number(d.price) * pdpQty(el);
    var counts = d.counts === 'true';
    var over = eligible >= threshold;
    var after = counts ? eligible + price : eligible;
    var mode;
    if (d.inbag === 'true' && over) mode = 'added';
    else if (over && d.declined === 'true') mode = 'declined';
    else if (over) mode = 'adding';
    else if (!counts) mode = 'excluded';
    else if (after >= threshold) mode = 'unlocks';
    else mode = 'locked';

    var p = Math.min(100, (eligible / threshold) * 100);
    var t = over ? p : Math.min(100, (after / threshold) * 100);
    el.style.setProperty('--ahc-gwp-p', p.toFixed(1) + '%');
    el.style.setProperty('--ahc-gwp-t', t.toFixed(1) + '%');
    var ship = Number(d.ship);
    d.shipDone = ship > 0 && Number(d.total) >= ship ? 'true' : 'false';

    var amount = mode === 'locked' ? money(threshold - after, S.currency) : '';
    var sig = mode + '|' + amount;
    if (sig === el.__sig) return; // nothing to redraw
    el.__sig = sig;
    d.mode = mode;
    var html;
    if (mode === 'locked') {
      html = String(S.locked || '').replace('[amount]', amount);
    } else if (mode === 'declined') {
      html = '<span></span><button type="button" class="ahc-gwp-btn" data-ahc-gwp-add></button>';
    } else {
      html = '';
    }
    el.querySelectorAll('[data-ahc-gwp-msg]').forEach(function (msg) {
      if (mode === 'locked') msg.innerHTML = html;
      else if (mode === 'declined') {
        msg.innerHTML = html;
        msg.querySelector('span').textContent = S.declined || '';
        msg.querySelector('button').textContent = S.add || '';
      } else msg.textContent = S[mode] || '';
    });
    var live = el.querySelector('[data-ahc-gwp-live]');
    if (live && (mode === 'added' || mode === 'unlocks')) live.textContent = S[mode] || '';
  }

  function pdpUpdate(s) {
    pdpBlocks.forEach(function (el) {
      el.dataset.eligible = s.eligible;
      el.dataset.inbag = s.lines.length > 0 ? 'true' : 'false';
      el.dataset.declined = s.declined ? 'true' : 'false';
      if (typeof s.total === 'number') el.dataset.total = s.total;
      el.hidden = !s.on || (!s.available && !s.lines.length);
      pdpRender(el);
    });
  }

  function pdpInit() {
    pdpBlocks = Array.prototype.slice.call(document.querySelectorAll('[data-ahc-gwp-pdp]'));
    pdpBlocks.forEach(function (el) {
      // Quick-add modals reuse product markup; the block is product-page only.
      if (el.closest('quick-cart-drawer, .quick-cart-drawer')) {
        el.remove();
        return;
      }
      try {
        el.__strings = JSON.parse(el.querySelector('[data-ahc-gwp-pdp-strings]').textContent);
      } catch (e) {
        el.__strings = {};
      }
    });
    pdpBlocks = pdpBlocks.filter(function (el) {
      return el.isConnected;
    });
    if (!pdpBlocks.length) return;

    // Size changes can change the price.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var v = event && event.data && event.data.variant;
        if (!v || typeof v.price !== 'number') return;
        pdpBlocks.forEach(function (el) {
          el.dataset.price = v.price;
          pdpRender(el);
        });
      });
    }
    // So can the quantity.
    var onQty = function (e) {
      if (!e.target || e.target.name !== 'quantity') return;
      setTimeout(function () {
        pdpBlocks.forEach(pdpRender);
      }, 0);
    };
    document.addEventListener('change', onQty);
    document.addEventListener('input', onQty);
    document.addEventListener('click', function (e) {
      if (e.target.closest && e.target.closest('quantity-input button, .quantity__button')) onQty({ target: { name: 'quantity' } });
    });
  }

  /* ---- Decide. ---- */
  var busy = false;
  var again = false;
  var timer = null;
  var writes = [];
  var gaveUp = false;
  var saidNo = false; // a no recorded on this page, even if writing it failed

  // Never more than 4 writes in 20s: a misconfigured discount can't loop the cart.
  function canWrite() {
    var now = Date.now();
    writes = writes.filter(function (t) {
      return now - t < 20000;
    });
    if (writes.length >= 4) return false;
    writes.push(now);
    return true;
  }

  function reconcile() {
    if (busy) {
      again = true;
      return;
    }
    busy = true;
    var wrote = false;
    readState()
      .then(function (s) {
        if (!s) return;
        pdpUpdate(s);
        if (!s.on) return;
        var over = s.eligible >= s.threshold;
        var auto = s.lines.filter(function (l) {
          return l.auto;
        });
        var hasGift = s.lines.length > 0;
        var declined = s.declined || saidNo;
        var last = lastSeen();

        // We had put it in, it's gone, and the bag is still over: the shopper said no.
        if (over && !hasGift && last.hadAuto && !declined) {
          remember(false);
          saidNo = true;
          if (!canWrite()) return;
          wrote = true;
          return setDeclined(true);
        }
        if (over && !hasGift && !declined && s.available && !gaveUp) {
          if (!canWrite()) return;
          wrote = true;
          return addGift(s.variant).then(function (res) {
            if (!res.ok) gaveUp = true; // sold out a moment ago: stop trying on this page
            remember(res.ok);
          });
        }
        if (!over && auto.length) {
          remember(false);
          if (!canWrite()) return;
          wrote = true;
          return removeLines(auto);
        }
        remember(auto.length > 0);
      })
      .catch(function () {})
      .then(function () {
        busy = false;
        // After our own write, read once more so the product page catches up.
        if (again || wrote) {
          again = false;
          soon();
        }
      });
  }

  function soon() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 120);
  }

  /* ---- The shopper deletes the gift: remove it and record the no in one request,
     so the drawer never flashes "adding" in between. ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.cart-item--ahc-gift cart-remove-button');
    if (!btn) return;
    var link = btn.querySelector('a[href]');
    var key = link && new URL(link.href, location.origin).searchParams.get('id');
    if (!key) return; // fall back to the theme's own remove; reconcile still catches the no
    e.preventDefault();
    e.stopPropagation();
    var items = btn.closest('cart-drawer-items') || btn.closest('cart-items');
    if (items) items.classList.add('is-loading');
    var updates = {};
    updates[key] = 0;
    remember(false);
    saidNo = true;
    post('cart/update.js', { updates: updates, attributes: { _gift_declined: '1' } })
      .catch(function () {})
      .then(function () {
        if (items) items.classList.remove('is-loading');
        soon();
      });
  }, true);

  /* ---- "Add it back" after a no (drawer or product page). ---- */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-ahc-gwp-add]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation(); // the drawer closes on outside clicks; a redrawn button counts as outside
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    saidNo = false;
    readState()
      .then(function (s) {
        if (!s || !s.on || !s.available) return;
        return origFetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { _gift_declined: '' } })
        }).then(function () {
          return addGift(s.variant).then(function (res) {
            remember(res.ok);
          });
        });
      })
      .catch(function () {})
      .then(function () {
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        soon();
      });
  }, true);

  /* ---- Sample switcher (samples theme only). It sits in the page flow, at the
     top of the drawer and above the product page block, so it never covers anything. ---- */
  var DESIGNS = [
    ['a', 'A', 'Two-stop bar'],
    ['b', 'B', 'Gift card'],
    ['c', 'C', 'Reserved slot']
  ];
  function setDesign(d) {
    document.documentElement.setAttribute('data-ahc-gwp-design', d);
    try {
      sessionStorage.setItem('ahc-gwp-design', d);
    } catch (err) {}
    document.querySelectorAll('.ahc-gwp-switch button').forEach(function (x) {
      x.setAttribute('aria-pressed', x.dataset.design === d ? 'true' : 'false');
    });
  }
  function makeSwitcher(extraClass) {
    var bar = document.createElement('div');
    bar.className = 'ahc-gwp-switch ' + extraClass;
    bar.setAttribute('role', 'group');
    bar.setAttribute('aria-label', 'Gift design sample');
    var label = document.createElement('span');
    label.textContent = 'Sample · gift design';
    bar.appendChild(label);
    var group = document.createElement('span');
    group.className = 'ahc-gwp-switch__group';
    var current = document.documentElement.getAttribute('data-ahc-gwp-design');
    DESIGNS.forEach(function (d) {
      var b = document.createElement('button');
      b.type = 'button';
      b.textContent = d[1];
      b.title = d[2];
      b.dataset.design = d[0];
      b.setAttribute('aria-pressed', current === d[0] ? 'true' : 'false');
      b.addEventListener('click', function (e) {
        e.stopPropagation();
        setDesign(d[0]);
      });
      group.appendChild(b);
    });
    bar.appendChild(group);
    return bar;
  }
  function switchers() {
    if (!CFG.switcher) return;
    var inner = document.querySelector('cart-drawer .cart-drawer__inner');
    if (inner && !inner.querySelector('.ahc-gwp-switch')) inner.insertBefore(makeSwitcher('ahc-gwp-switch--drawer'), inner.firstChild);
    pdpBlocks.forEach(function (el) {
      if (el.previousElementSibling && el.previousElementSibling.classList.contains('ahc-gwp-switch')) return;
      el.parentNode.insertBefore(makeSwitcher('ahc-gwp-switch--pdp'), el);
    });
  }

  /* ---- Start. ---- */
  watchCart(soon);
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) soon();
  });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') soon();
  });
  function start() {
    pdpInit();
    switchers();
    soon();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
